import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  parseISO,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';
import { useAppResource } from '../../context/AppDataContext';
import { auditService } from '../../services/auditService';
import { teamService } from '../../services/teamService';
import { userService } from '../../services/userService';
import { organizationService } from '../../services/organizationService';
import { outletService } from '../../services/outletService';
import ManageTeamModal from '../../components/Manpower/ManageTeamModal';
import '../../components/Manpower/Manpower.css';
import AuditScheduleConflicts from '../../components/AuditScheduleConflicts';
import OutletSearch from '../../components/OutletSearch';
import MasterFileUploader from '../../components/MasterFileUploader';
import { getRoleLabel } from '../../utils/permissions';
import DataGrid, { actionsColumn, dash } from '../../components/DataGrid';
import ScheduleStatus, {
  isScheduleClosed,
  SCHEDULE_STATUS_LABEL,
  scheduleStatusOf,
} from '../../components/ScheduleStatus';
import './Audits.css';
import { PageLoader } from '../../components/Loader';

const VIEWS = ['month', 'week', 'day', 'list'];
const REMINDER_OPTIONS = [
  { value: 15, label: '15 minutes before' },
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
  { value: 120, label: '2 hours before' },
  { value: 1440, label: '1 day before' },
];

/** "20 people · Team A, Team B + 3 individually" for a schedule's team. */
function teamSummary(audit) {
  const teams = audit?.teams || [];
  const people = new Set(teams.flatMap((team) => (team.members || []).map((m) => String(m.id ?? m.user_id))));
  const shared = teams.filter((team) => !team.is_dynamic).map((team) => team.name);
  const own = teams.find((team) => team.is_dynamic);
  const parts = [shared.join(', '), own?.members?.length ? `${own.members.length} added individually` : '']
    .filter(Boolean)
    .join(' + ');
  return { people: people.size, teams: parts };
}

const emptyForm = {
  name: '',
  organization_id: '',
  outlet_id: '',
  client_ids: [],
  start_date: '',
  start_time: '09:00',
  end_date: '',
  end_time: '17:00',
  reminder_start_enabled: false,
  reminder_start_minutes: 60,
  reminder_end_enabled: false,
  reminder_end_minutes: 60,
  team_ids: [],
  use_dynamic_team: false,
  dynamic_team_name: '',
  dynamic_team_member_ids: [],
  lead_auditor_id: '',
};

function combineDateTime(date, time) {
  if (!date || !time) return null;
  return new Date(`${date}T${time}:00`).toISOString();
}

function splitDateTime(value) {
  if (!value) {
    return { date: '', time: '09:00' };
  }
  const parsed = typeof value === 'string' ? parseISO(value) : new Date(value);
  return {
    date: format(parsed, 'yyyy-MM-dd'),
    time: format(parsed, 'HH:mm'),
  };
}

/** "Outlet name (CODE) · City" for pickers and summaries. */
function describeOutlet(outlet) {
  if (!outlet) return '';
  const code = outlet.outlet_code ? ` (${outlet.outlet_code})` : '';
  const city = outlet.city ? ` · ${outlet.city}` : '';
  return `${outlet.name}${code}${city}`;
}

/** "Organization · Outlet (CODE)" for an audit row, or '' when not set. */
function auditPlace(audit) {
  const outlet = audit.outlet_name
    ? `${audit.outlet_name}${audit.outlet_code ? ` (${audit.outlet_code})` : ''}`
    : '';
  return [audit.organization_name, outlet].filter(Boolean).join(' · ');
}

function auditsForDay(audits, day) {
  return audits.filter((audit) => {
    const start = parseISO(audit.start_at);
    const end = parseISO(audit.end_at);
    return (
      isSameDay(start, day) ||
      isSameDay(end, day) ||
      isWithinInterval(day, { start, end })
    );
  });
}

function Audits() {
  const [error, setError] = useState('');
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());

  const routerNavigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formOpen, setFormOpen] = useState(false);
  const [editingAudit, setEditingAudit] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [masterFileMeta, setMasterFileMeta] = useState(null);
  const [scheduleConflicts, setScheduleConflicts] = useState([]);
  const [teamAudit, setTeamAudit] = useState(null);

  const formSchedule = useMemo(
    () => ({
      startAt: combineDateTime(form.start_date, form.start_time),
      endAt: combineDateTime(form.end_date, form.end_time),
    }),
    [form.start_date, form.start_time, form.end_date, form.end_time],
  );

  const conflictMemberCount = useMemo(
    () => new Set(scheduleConflicts.map((item) => item.user_id)).size,
    [scheduleConflicts],
  );

  const handleConflictsChange = useCallback((conflicts) => {
    setScheduleConflicts(conflicts);
  }, []);

  const range = useMemo(() => {
    if (view === 'month' || view === 'list') {
      const start = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 });
      const end = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });
      return { start, end };
    }
    if (view === 'week') {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 1 }),
        end: endOfWeek(currentDate, { weekStartsOn: 1 }),
      };
    }
    return {
      start: currentDate,
      end: addDays(currentDate, 1),
    };
  }, [view, currentDate]);

  const rangeStart = range.start.toISOString();
  const rangeEnd = range.end.toISOString();
  const auditsKey = `audits:${rangeStart}:${rangeEnd}`;
  const loadAudits = useCallback(
    () =>
      auditService.getAll({
        from: rangeStart,
        to: rangeEnd,
      }),
    [rangeStart, rangeEnd],
  );
  const loadTeams = useCallback(() => teamService.getAll(), []);
  const loadUsers = useCallback(() => userService.getAll(), []);
  const loadOrganizations = useCallback(() => organizationService.getAll(), []);
  const loadOutlets = useCallback(() => outletService.getAll(), []);
  const { data: organizations = [] } = useAppResource(
    'organizations',
    loadOrganizations,
    [],
  );
  const { data: outlets = [] } = useAppResource('outlets', loadOutlets, []);
  const outletsForOrg = useMemo(
    () =>
      outlets
        .filter(
          (outlet) =>
            !form.organization_id ||
            String(outlet.organization_id) === String(form.organization_id),
        )
        .sort((a, b) => String(a.name).localeCompare(String(b.name))),
    [outlets, form.organization_id],
  );
  const selectedOrganization = organizations.find(
    (org) => String(org.id) === String(form.organization_id),
  );
  const selectedOutlet = outlets.find(
    (outlet) => String(outlet.id) === String(form.outlet_id),
  );
  const {
    data: audits = [],
    loading: auditsLoading,
    error: auditsError,
    refresh: refreshAudits,
  } = useAppResource(auditsKey, loadAudits, [loadAudits]);
  const {
    loading: teamsLoading,
    error: teamsError,
    refresh: refreshTeams,
  } = useAppResource('teams', loadTeams, []);
  const {
    data: users = [],
    loading: usersLoading,
    error: usersError,
  } = useAppResource('users', loadUsers, []);
  const loading = auditsLoading || teamsLoading || usersLoading;
  const visibleError = error || auditsError || teamsError || usersError;

  const clientUsers = useMemo(
    () =>
      users
        .filter((user) => user.role_name === 'client')
        .sort((a, b) => String(a.name || a.email).localeCompare(String(b.name || b.email))),
    [users],
  );
  // Clients who see this audit automatically: organization-level clients of
  // its organization, and outlet-level clients (store / area managers) of its outlet.
  const autoClients = useMemo(
    () =>
      clientUsers.filter((user) => {
        if (!user.client_organization_id) return false;
        if (user.client_access_level === 'outlet') {
          return Boolean(form.outlet_id) &&
            (user.client_outlets || []).some((outlet) => String(outlet.id) === String(form.outlet_id));
        }
        return Boolean(form.organization_id) &&
          String(user.client_organization_id) === String(form.organization_id);
      }),
    [clientUsers, form.organization_id, form.outlet_id],
  );
  // Lead stock auditor in charge: auditors (team leads first) and admins.
  const leadOptions = useMemo(
    () =>
      users
        .filter((user) => ['auditor', 'admin', 'super_admin'].includes(user.role_name))
        .sort((a, b) =>
          Number(Boolean(b.is_manager)) - Number(Boolean(a.is_manager)) ||
          String(a.name || a.email).localeCompare(String(b.name || b.email)),
        ),
    [users],
  );

  const calendarDays = useMemo(() => {
    if (view !== 'month') return [];
    return eachDayOfInterval({ start: range.start, end: range.end });
  }, [view, range]);

  const weekDays = useMemo(() => {
    if (view !== 'week') return [];
    return eachDayOfInterval({ start: range.start, end: range.end });
  }, [view, range]);

  const sortedAudits = useMemo(
    () =>
      [...audits].sort(
        (a, b) => new Date(a.start_at) - new Date(b.start_at),
      ),
    [audits],
  );

  const navigate = (direction) => {
    if (view === 'month' || view === 'list') {
      setCurrentDate((prev) =>
        direction > 0 ? addMonths(prev, 1) : subMonths(prev, 1),
      );
      return;
    }
    if (view === 'week') {
      setCurrentDate((prev) =>
        direction > 0 ? addWeeks(prev, 1) : subWeeks(prev, 1),
      );
      return;
    }
    setCurrentDate((prev) =>
      direction > 0 ? addDays(prev, 1) : subDays(prev, 1),
    );
  };

  const openCreateForm = (day) => {
    const base = day || currentDate;
    const dateStr = format(base, 'yyyy-MM-dd');
    setEditingAudit(null);
    setMasterFileMeta(null);
    setScheduleConflicts([]);
    setForm({
      ...emptyForm,
      start_date: dateStr,
      end_date: dateStr,
    });
    setFormOpen(true);
  };

  const openEditForm = async (audit) => {
    const start = splitDateTime(audit.start_at);
    const end = splitDateTime(audit.end_at);
    const existingTeamIds = (audit.teams || [])
      .filter((team) => !team.is_dynamic)
      .map((team) => String(team.id));
    const dynamicTeam = (audit.teams || []).find((team) => team.is_dynamic);

    setEditingAudit(audit);
    setScheduleConflicts([]);
    setForm({
      name: audit.name || '',
      organization_id: audit.organization_id ? String(audit.organization_id) : '',
      outlet_id: audit.outlet_id ? String(audit.outlet_id) : '',
      client_ids: (audit.clients || []).map((client) => String(client.id)),
      start_date: start.date,
      start_time: start.time,
      end_date: end.date,
      end_time: end.time,
      reminder_start_enabled: Boolean(audit.reminder_start_enabled),
      reminder_start_minutes: audit.reminder_start_minutes || 60,
      reminder_end_enabled: Boolean(audit.reminder_end_enabled),
      reminder_end_minutes: audit.reminder_end_minutes || 60,
      team_ids: existingTeamIds,
      use_dynamic_team: Boolean(dynamicTeam),
      dynamic_team_name: dynamicTeam?.name || '',
      dynamic_team_member_ids: (dynamicTeam?.members || []).map((m) =>
        String(m.id),
      ),
      lead_auditor_id: audit.lead_auditor_id ? String(audit.lead_auditor_id) : '',
    });
    setFormOpen(true);

    try {
      const meta = await auditService.getMasterFile(audit.id);
      setMasterFileMeta(meta);
    } catch (err) {
      setMasterFileMeta(null);
    }
  };

  // /audits?edit=<id> (from the setup wizard) opens that audit's form.
  const editParam = searchParams.get('edit');
  useEffect(() => {
    if (!editParam) return;
    setSearchParams({}, { replace: true });
    auditService
      .getById(editParam)
      .then((audit) => openEditForm(audit))
      .catch((err) => setError(err.message));
    // openEditForm is recreated each render; run once per edit request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editParam]);

  // A closed schedule opens read-only (the server refuses edits too).
  const scheduleLocked = Boolean(editingAudit) && isScheduleClosed(editingAudit);

  const closeForm = () => {
    setFormOpen(false);
    setEditingAudit(null);
    setForm(emptyForm);
    setMasterFileMeta(null);
    setScheduleConflicts([]);
  };

  const getAuditPortalHref = (auditId) => `/audits/${auditId}/portal`;
  const getSetupHref = (auditId) => `/audits/${auditId}/setup`;

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    const startAt = combineDateTime(form.start_date, form.start_time);
    const endAt = combineDateTime(form.end_date, form.end_time);

    if (!form.organization_id || !form.outlet_id) {
      setError('Choose the organization and the outlet this schedule is for.');
      setSaving(false);
      return;
    }

    if (!startAt || !endAt) {
      setError('Start and end date/time are required.');
      setSaving(false);
      return;
    }

    if (new Date(endAt) <= new Date(startAt)) {
      setError('End must be after start.');
      setSaving(false);
      return;
    }

    if (conflictMemberCount > 0) {
      const confirmed = window.confirm(
        `${conflictMemberCount} team member${conflictMemberCount === 1 ? '' : 's'} overlap with another schedule in this period. Save anyway? You can adjust members after saving.`,
      );

      if (!confirmed) {
        setSaving(false);
        return;
      }
    }

    const payload = {
      name: form.name.trim(),
      organization_id: Number(form.organization_id),
      outlet_id: Number(form.outlet_id),
      // Only clients of this audit's organization keep a direct share.
      client_ids: form.client_ids
        .filter((id) =>
          clientUsers.some(
            (user) =>
              String(user.id) === String(id) &&
              String(user.client_organization_id) === String(form.organization_id),
          ),
        )
        .map(Number),
      start_at: startAt,
      end_at: endAt,
      reminder_start_enabled: form.reminder_start_enabled,
      reminder_start_minutes: Number(form.reminder_start_minutes),
      reminder_end_enabled: form.reminder_end_enabled,
      reminder_end_minutes: Number(form.reminder_end_minutes),
      lead_auditor_id: form.lead_auditor_id ? Number(form.lead_auditor_id) : null,
    };
    // Teams and members are managed in "Manage team" / setup step 4, not here.

    try {
      let created = null;
      if (editingAudit) {
        await auditService.update(editingAudit.id, payload);
      } else {
        created = await auditService.create(payload);
      }
      closeForm();
      await Promise.all([refreshAudits(), refreshTeams()]);
      // New audit: continue with the setup wizard (AMD, locations, book stock).
      if (created?.id) {
        routerNavigate(`/audits/${created.id}/setup?step=2`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (audit) => {
    const confirmed = window.confirm(`Delete schedule "${audit.name}"?`);
    if (!confirmed) return;

    setError('');
    try {
      await auditService.delete(audit.id);
      await refreshAudits();
    } catch (err) {
      setError(err.message);
    }
  };

  const headerLabel = useMemo(() => {
    if (view === 'month' || view === 'list') {
      return format(currentDate, 'MMMM yyyy');
    }
    if (view === 'week') {
      return `${format(range.start, 'MMM d')} – ${format(range.end, 'MMM d, yyyy')}`;
    }
    return format(currentDate, 'EEEE, MMMM d, yyyy');
  }, [view, currentDate, range]);

  const renderAuditChip = (audit, compact = false) => (
    <button
      key={audit.id}
      type="button"
      className={`audit-chip ${compact ? 'audit-chip--compact' : ''}`}
      onClick={() => openEditForm(audit)}
      title={`${audit.name}${auditPlace(audit) ? ` · ${auditPlace(audit)}` : ''} · ${format(parseISO(audit.start_at), 'HH:mm')} – ${format(parseISO(audit.end_at), 'HH:mm')}`}
    >
      {!compact && (
        <span className="audit-chip__time">
          {format(parseISO(audit.start_at), 'HH:mm')}
        </span>
      )}
      <span className="audit-chip__name">
        {audit.outlet_name ? `${audit.outlet_name} — ${audit.name}` : audit.name}
      </span>
    </button>
  );

  const dateColumn = (headerName, key) => ({
    headerName,
    colId: key,
    filter: 'agDateColumnFilter',
    valueGetter: (p) => (p.data[key] ? parseISO(p.data[key]) : null),
    valueFormatter: (p) => (p.value ? format(p.value, 'MMM d, yyyy HH:mm') : '—'),
    getQuickFilterText: (p) => (p.value ? format(p.value, 'MMM d, yyyy HH:mm') : ''),
    minWidth: 170,
  });

  const auditColumns = [
    {
      headerName: 'Schedule No',
      colId: 'schedule_no',
      valueGetter: (p) => p.data.schedule_no || '',
      valueFormatter: (p) => dash(p.value),
      cellClass: 'audits-list__schedule-no',
      minWidth: 160,
      pinned: 'left',
    },
    {
      headerName: 'Status',
      colId: 'schedule_status',
      valueGetter: (p) => SCHEDULE_STATUS_LABEL[scheduleStatusOf(p.data)] || '',
      cellRenderer: (p) => <ScheduleStatus audit={p.data} />,
      minWidth: 150,
      maxWidth: 170,
    },
    { headerName: 'Name', field: 'name', minWidth: 180 },
    {
      headerName: 'Organization · Outlet',
      colId: 'place',
      valueGetter: (p) => auditPlace(p.data) || '',
      valueFormatter: (p) => dash(p.value),
      minWidth: 220,
    },
    dateColumn('Start', 'start_at'),
    dateColumn('End', 'end_at'),
    {
      headerName: 'Teams',
      colId: 'teams',
      valueGetter: (p) => (p.data.teams || []).map((team) => team.name).join(', '),
      valueFormatter: (p) => dash(p.value),
      wrapText: true,
      autoHeight: true,
    },
    {
      headerName: 'Reminders',
      colId: 'reminders',
      maxWidth: 150,
      valueGetter: (p) =>
        [p.data.reminder_start_enabled && 'Start', p.data.reminder_end_enabled && 'End']
          .filter(Boolean)
          .join(' '),
      cellRenderer: (p) =>
        p.data.reminder_start_enabled || p.data.reminder_end_enabled ? (
          <>
            {p.data.reminder_start_enabled && <span className="audit-reminder-badge">Start</span>}
            {p.data.reminder_end_enabled && <span className="audit-reminder-badge">End</span>}
          </>
        ) : (
          '—'
        ),
    },
    actionsColumn(
      (audit) => (
        <>
          <a
            href={getAuditPortalHref(audit.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="audits-list__sync"
          >
            Open portal
          </a>
          <Link to={getSetupHref(audit.id)} className="audits-list__setup">
            Setup
          </Link>
          <button type="button" onClick={() => setTeamAudit(audit)} title="Who scans this schedule">
            Team
          </button>
          <button
            type="button"
            onClick={() => openEditForm(audit)}
            title={isScheduleClosed(audit) ? 'Closed schedules can be viewed but not edited' : 'Edit schedule'}
          >
            {isScheduleClosed(audit) ? 'View' : 'Edit'}
          </button>
          <button
            type="button"
            className="audits-list__delete"
            onClick={() => handleDelete(audit)}
          >
            Delete
          </button>
        </>
      ),
      { width: 400, className: 'audits-list__actions' },
    ),
  ];

  return (
    <div className="page audits-page">
      <header className="page__header audits-page__header">
        <div>
          <p className="page__eyebrow">Operations</p>
          <h1>Schedules</h1>
          <p className="audits-page__subtitle">
            Plan schedules on the calendar, assign teams, and configure reminders.
          </p>
        </div>

        <button
          type="button"
          className="audits-page__add-btn"
          onClick={() => openCreateForm()}
        >
          + New Schedule
        </button>
      </header>

      {visibleError && (
        <div className="audits-page__alert" role="alert">
          {visibleError}
        </div>
      )}

      <section className="audits-toolbar">
        <div className="audits-toolbar__nav">
          <button type="button" onClick={() => navigate(-1)} aria-label="Previous">
            ←
          </button>
          <button type="button" onClick={() => setCurrentDate(new Date())}>
            Today
          </button>
          <button type="button" onClick={() => navigate(1)} aria-label="Next">
            →
          </button>
          <strong>{headerLabel}</strong>
        </div>

        <div className="audits-toolbar__views">
          {VIEWS.map((item) => (
            <button
              key={item}
              type="button"
              className={view === item ? 'audits-toolbar__view--active' : ''}
              onClick={() => setView(item)}
            >
              {item.charAt(0).toUpperCase() + item.slice(1)}
            </button>
          ))}
        </div>
      </section>

      <section className="audits-page__card">
        {loading ? (
          <PageLoader label="Loading schedules…" />
        ) : view === 'month' ? (
          <div className="audit-calendar audit-calendar--month">
            <div className="audit-calendar__weekdays">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                <div key={d}>{d}</div>
              ))}
            </div>
            <div className="audit-calendar__grid">
              {calendarDays.map((day) => {
                const dayAudits = auditsForDay(audits, day);
                const outside = !isSameMonth(day, currentDate);
                return (
                  <div
                    key={day.toISOString()}
                    className={`audit-calendar__cell ${outside ? 'audit-calendar__cell--outside' : ''}`}
                  >
                    <button
                      type="button"
                      className="audit-calendar__day"
                      onClick={() => openCreateForm(day)}
                    >
                      {format(day, 'd')}
                    </button>
                    <div className="audit-calendar__events">
                      {dayAudits.slice(0, 3).map((audit) =>
                        renderAuditChip(audit, true),
                      )}
                      {dayAudits.length > 3 && (
                        <span className="audit-calendar__more">
                          +{dayAudits.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : view === 'week' ? (
          <div className="audit-calendar audit-calendar--week">
            {weekDays.map((day) => {
              const dayAudits = auditsForDay(audits, day);
              return (
                <div key={day.toISOString()} className="audit-calendar__week-col">
                  <header>
                    <span>{format(day, 'EEE')}</span>
                    <button type="button" onClick={() => openCreateForm(day)}>
                      {format(day, 'd')}
                    </button>
                  </header>
                  <div className="audit-calendar__week-events">
                    {dayAudits.length === 0 ? (
                      <p className="audit-calendar__none">No schedules</p>
                    ) : (
                      dayAudits.map((audit) => renderAuditChip(audit))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : view === 'day' ? (
          <div className="audit-calendar audit-calendar--day">
            <header className="audit-calendar__day-header">
              <h2>{format(currentDate, 'EEEE, MMMM d')}</h2>
              <button type="button" onClick={() => openCreateForm(currentDate)}>
                + Add schedule
              </button>
            </header>
            <div className="audit-calendar__day-list">
              {auditsForDay(audits, currentDate).length === 0 ? (
                <p className="audits-page__empty">No schedules for this day.</p>
              ) : (
                auditsForDay(audits, currentDate).map((audit) => (
                  <article key={audit.id} className="audit-day-card">
                    <div>
                      <p className="audit-day-card__schedule">
                        <span className="audits-list__schedule-no">{audit.schedule_no || ''}</span>{' '}
                        <ScheduleStatus audit={audit} />
                      </p>
                      <h3>{audit.name}</h3>
                      <p className="audit-day-card__place">
                        {auditPlace(audit) || 'No organization or outlet set'}
                      </p>
                      {(audit.clients || []).length > 0 && (
                        <p className="audit-day-card__teams">
                          Also shared with: {audit.clients.map((client) => client.name || client.email).join(', ')}
                        </p>
                      )}
                      <p>
                        {format(parseISO(audit.start_at), 'HH:mm')} –{' '}
                        {format(parseISO(audit.end_at), 'HH:mm')}
                      </p>
                      {(audit.teams || []).length > 0 && (
                        <p className="audit-day-card__teams">
                          Teams:{' '}
                          {audit.teams.map((team) => team.name).join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="audit-day-card__actions">
                      <Link to={getSetupHref(audit.id)} className="audit-day-card__setup">
                        Setup
                      </Link>
                      <button type="button" onClick={() => setTeamAudit(audit)}>
                        Team
                      </button>
                      <button type="button" onClick={() => openEditForm(audit)}>
                        {isScheduleClosed(audit) ? 'View' : 'Edit'}
                      </button>
                      <button
                        type="button"
                        className="audit-day-card__delete"
                        onClick={() => handleDelete(audit)}
                      >
                        Delete
                      </button>
                    </div>
                  </article>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="audits-list-wrap">
            <DataGrid
              rowData={sortedAudits}
              columnDefs={auditColumns}
              searchPlaceholder="Search schedule, organization, outlet or team…"
              emptyMessage="No schedules in this period."
            />
          </div>
        )}
      </section>

      {formOpen && (
        <div className="audits-modal">
          <div className="audits-modal__backdrop" onClick={closeForm} />
          <div className="audits-modal__panel" role="dialog" aria-modal="true">
            <div className="audits-modal__header">
              <div>
                <h2>
                  {editingAudit ? (scheduleLocked ? 'Schedule (closed)' : 'Edit Schedule') : 'New Schedule'}
                </h2>
                {editingAudit ? (
                  <p className="audits-modal__schedule">
                    <span className="audits-list__schedule-no">
                      {editingAudit.schedule_no || 'No schedule number'}
                    </span>{' '}
                    <ScheduleStatus audit={editingAudit} />
                  </p>
                ) : null}
                {conflictMemberCount > 0 && (
                  <span className="audits-modal__conflict-badge">
                    {conflictMemberCount} schedule clash
                    {conflictMemberCount === 1 ? '' : 'es'}
                  </span>
                )}
              </div>
              <button type="button" onClick={closeForm}>
                ×
              </button>
            </div>

            {scheduleLocked ? (
              <div className="audits-form__locked" role="note">
                This schedule is closed (its end time has passed), so it can no longer be edited.
              </div>
            ) : null}

            <form className="audits-form" onSubmit={handleSubmit}>
              <fieldset className="audits-form__lock" disabled={scheduleLocked}>
              <label>
                Schedule name
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="Monthly stock audit"
                />
              </label>

              <fieldset className="audits-form__place">
                <legend>Where is this schedule?</legend>
                <div className="audits-form__grid">
                  <label>
                    Organization
                    <select
                      name="organization_id"
                      value={form.organization_id}
                      onChange={(event) => {
                        const value = event.target.value;
                        setForm((prev) => {
                          const outlet = outlets.find(
                            (item) => String(item.id) === String(prev.outlet_id),
                          );
                          const keepOutlet =
                            outlet && String(outlet.organization_id) === String(value);
                          return {
                            ...prev,
                            organization_id: value,
                            outlet_id: keepOutlet ? prev.outlet_id : '',
                          };
                        });
                      }}
                      required
                    >
                      <option value="">Select organization…</option>
                      {organizations.map((org) => (
                        <option key={org.id} value={String(org.id)}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="audits-form__outlet">
                    <span>Outlet</span>
                    <OutletSearch
                      outlets={outletsForOrg}
                      value={form.outlet_id}
                      describe={describeOutlet}
                      onChange={(outlet) =>
                        setForm((prev) => ({
                          ...prev,
                          outlet_id: outlet ? String(outlet.id) : '',
                          organization_id: outlet
                            ? String(outlet.organization_id)
                            : prev.organization_id,
                        }))
                      }
                    />
                  </div>
                </div>
                {form.organization_id && outletsForOrg.length === 0 ? (
                  <p className="audits-form__hint">
                    This organization has no outlets yet. Add one on the Outlets page first.
                  </p>
                ) : null}
                {selectedOutlet ? (
                  <p className="audits-form__place-summary">
                    Schedule at <strong>{selectedOrganization?.name || '—'}</strong> ·{' '}
                    <strong>{describeOutlet(selectedOutlet)}</strong>
                    {selectedOutlet.address ? ` · ${selectedOutlet.address}` : ''}
                  </p>
                ) : null}
              </fieldset>

              <fieldset className="audits-form__clients">
                <legend>Client access</legend>
                <p className="audits-form__hint">
                  Clients follow this schedule's portal (live counts and comparisons), read-only.
                  Who sees it is set on the Clients page by organization and outlet.
                </p>
                {!form.outlet_id ? (
                  <p className="audits-form__hint">Choose the organization and outlet to see who can view it.</p>
                ) : autoClients.length === 0 ? (
                  <p className="audits-form__hint">
                    No clients of this organization are linked to this outlet yet. Add them on
                    the Clients page.
                  </p>
                ) : (
                  <div className="audits-form__auto-clients">
                    <strong>
                      Can view this schedule ({autoClients.length})
                    </strong>
                    <ul>
                      {autoClients.map((user) => (
                        <li key={user.id}>
                          <span>{user.name || user.email}</span>
                          <small>
                            {[
                              user.email,
                              user.client_title,
                              user.client_access_level === 'outlet' ? 'this outlet' : 'whole organization',
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </small>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {clientUsers.length === 0 ? (
                  <p className="audits-form__hint">No clients yet. Add them on the Clients page.</p>
                ) : null}
              </fieldset>

              <div className="audits-form__grid">
                <label>
                  Start date
                  <input
                    type="date"
                    name="start_date"
                    value={form.start_date}
                    onChange={handleChange}
                    required
                  />
                </label>
                <label>
                  Start time
                  <input
                    type="time"
                    name="start_time"
                    value={form.start_time}
                    onChange={handleChange}
                    required
                  />
                </label>
              </div>

              <div className="audits-form__grid">
                <label>
                  End date
                  <input
                    type="date"
                    name="end_date"
                    value={form.end_date}
                    onChange={handleChange}
                    required
                  />
                </label>
                <label>
                  End time
                  <input
                    type="time"
                    name="end_time"
                    value={form.end_time}
                    onChange={handleChange}
                    required
                  />
                </label>
              </div>

              <label className="audits-form__lead">
                Lead stock auditor in charge
                <select
                  name="lead_auditor_id"
                  value={form.lead_auditor_id}
                  onChange={handleChange}
                >
                  <option value="">Not set</option>
                  {leadOptions.map((user) => (
                    <option key={user.id} value={String(user.id)}>
                      {user.name || user.email}
                      {` (${getRoleLabel(user)})`}
                    </option>
                  ))}
                </select>
                <span className="audits-form__hint">
                  Shown on the schedule portal and receives recount requests on their phone.
                </span>
              </label>

              <fieldset className="audits-form__reminders">
                <legend>Reminders</legend>
                <label className="audits-form__checkbox">
                  <input
                    type="checkbox"
                    name="reminder_start_enabled"
                    checked={form.reminder_start_enabled}
                    onChange={handleChange}
                  />
                  <span>Send reminder before start</span>
                </label>
                {form.reminder_start_enabled && (
                  <label>
                    Start reminder timing
                    <select
                      name="reminder_start_minutes"
                      value={form.reminder_start_minutes}
                      onChange={handleChange}
                    >
                      {REMINDER_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <label className="audits-form__checkbox">
                  <input
                    type="checkbox"
                    name="reminder_end_enabled"
                    checked={form.reminder_end_enabled}
                    onChange={handleChange}
                  />
                  <span>Send reminder before end</span>
                </label>
                {form.reminder_end_enabled && (
                  <label>
                    End reminder timing
                    <select
                      name="reminder_end_minutes"
                      value={form.reminder_end_minutes}
                      onChange={handleChange}
                    >
                      {REMINDER_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </fieldset>

              <fieldset className="audits-form__teams">
                <legend>Team</legend>
                {editingAudit ? (
                  <>
                    <div className="team-summary">
                      <div>
                        <p>
                          <strong>{teamSummary(editingAudit).people}</strong> people assigned
                          {teamSummary(editingAudit).teams ? ` · ${teamSummary(editingAudit).teams}` : ''}
                        </p>
                        <small>Add or remove people, set the lead and who is off or replaced on some days (Manage team shows the count after those changes).</small>
                      </div>
                      <button type="button" className="bulk-open-btn" onClick={() => setTeamAudit(editingAudit)}>
                        Manage team
                      </button>
                    </div>
                    <AuditScheduleConflicts
                      startAt={formSchedule.startAt}
                      endAt={formSchedule.endAt}
                      teamIds={form.team_ids}
                      dynamicMemberIds={form.use_dynamic_team ? form.dynamic_team_member_ids : []}
                      excludeAuditId={editingAudit?.id}
                      onConflictsChange={handleConflictsChange}
                      isEditing
                    />
                  </>
                ) : (
                  <p className="audits-form__hint">
                    You add the team after saving: the setup wizard opens with a Manpower step (people, teams,
                    lead auditor and clashes).
                  </p>
                )}
              </fieldset>

              {editingAudit && (
                <fieldset className="audits-form__master-files">
                  <legend>Master file</legend>
                  <p className="audits-form__hint">
                    Upload the client's article master (AMD) as .txt, CSV, .xlsx or .xlsb,
                    then choose which columns hold the EAN, article code and description.
                    Phones get the EAN and description.
                  </p>

                  <div className="audits-form__master-item">

                    {masterFileMeta?.row_count != null ? (
                      <p className="audits-form__master-status">
                        Synced {Number(masterFileMeta.row_count).toLocaleString()} products
                        · {masterFileMeta.original_filename || 'Uploaded file'}
                        {masterFileMeta.uploaded_at
                          ? ` · ${new Date(masterFileMeta.uploaded_at).toLocaleString()}`
                          : ''}
                      </p>
                    ) : (
                      <p className="audits-form__master-status audits-form__master-status--empty">
                        No master file synced yet
                      </p>
                    )}

                    <MasterFileUploader
                      auditId={editingAudit.id}
                      storeCode={editingAudit.outlet_code || ''}
                      onUploaded={async () => {
                        try {
                          setMasterFileMeta(await auditService.getMasterFile(editingAudit.id));
                        } catch {
                          // The status line refreshes next time the form opens.
                        }
                      }}
                    />

                    <a
                      href={getAuditPortalHref(editingAudit.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="audits-form__portal-link"
                    >
                      Open schedule portal →
                    </a>
                    <Link to={getSetupHref(editingAudit.id)} className="audits-form__portal-link">
                      Setup wizard (AMD, locations, book stock) →
                    </Link>
                  </div>
                </fieldset>
              )}

              </fieldset>

              <div className="audits-form__actions">
                {editingAudit && (
                  <button
                    type="button"
                    className="audits-form__delete"
                    onClick={() => {
                      closeForm();
                      handleDelete(editingAudit);
                    }}
                  >
                    Delete
                  </button>
                )}
                <button
                  type="button"
                  className="audits-form__cancel"
                  onClick={closeForm}
                >
                  Cancel
                </button>
                {scheduleLocked ? null : (
                  <button
                    type="submit"
                    className="audits-form__submit"
                    disabled={saving}
                  >
                    {saving
                      ? 'Saving...'
                      : editingAudit
                        ? 'Update Schedule'
                        : 'Create Schedule'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
      {teamAudit ? (
        <ManageTeamModal
          auditId={teamAudit.id}
          auditName={teamAudit.name}
          onClose={() => setTeamAudit(null)}
          onSaved={async (saved) => {
            // Keep the open schedule form in step with the new lead auditor / team.
            if (saved && editingAudit && String(editingAudit.id) === String(saved.id)) {
              setEditingAudit(saved);
              setForm((prev) => ({
                ...prev,
                lead_auditor_id: saved.lead_auditor_id ? String(saved.lead_auditor_id) : '',
                team_ids: (saved.teams || []).filter((t) => !t.is_dynamic).map((t) => String(t.id)),
                use_dynamic_team: (saved.teams || []).some((t) => t.is_dynamic),
                dynamic_team_member_ids: ((saved.teams || []).find((t) => t.is_dynamic)?.members || []).map((m) => String(m.id)),
              }));
            }
            await Promise.all([refreshAudits(), refreshTeams()]);
          }}
        />
      ) : null}
    </div>
  );
}

export default Audits;
